import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HTTP_CP_ListSignInCompanies_Response_Company } from '@api-new/authservice';
import { HTTP_CP_LoadSignInBranding_Response } from '@api-new/companyservice';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { AppState } from '@shared/models/app-state.model';
import { AuthService } from '@shared/services/auth.service';
import {
  authError,
  checkLoginMail,
  checkManualLoginCode,
  hideNewCodeSent,
  loginWithToken,
  resetStore,
  selectSignInCompany,
  sendVerificationCode,
} from '@shared/store/auth/auth.action';
import {
  isIncorrectSignInCode,
  selectAuthLoading,
  selectBranding,
  selectIsCheckingLoginEmail,
  selectIsLoadingSignInCompanies,
  selectIsLoginFromParamsOrBrandingLoading,
  selectIsNewCodeSent,
  selectIsSendingVerificationCode,
  selectLastSignInCode,
  selectLastTimeVerificationCodeSent,
  selectNoEmailFoundError,
  selectSignInCompanies,
  selectSignInEmail,
  signInErrorCode,
} from '@shared/store/auth/auth.selectors';
import { Observable } from 'rxjs';

@Injectable()
export class AuthFacadeService {
  private readonly globalStore = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  readonly email$: Observable<string> = this.globalStore.select(selectSignInEmail);
  readonly loading$: Observable<boolean> = this.globalStore.select(selectAuthLoading);
  readonly isLoginFromParamsOrBrandingLoading$: Observable<boolean> = this.globalStore.select(selectIsLoginFromParamsOrBrandingLoading);
  readonly authPagesBranding$: Observable<HTTP_CP_LoadSignInBranding_Response> = this.globalStore.select(selectBranding);
  readonly isSendingVerificationCode$: Observable<boolean> = this.globalStore.select(selectIsSendingVerificationCode);
  readonly authError$: Observable<string> = this.globalStore.select(signInErrorCode);
  readonly lastTimeVerificationCodeSent$: Observable<Date> = this.globalStore.select(selectLastTimeVerificationCodeSent);
  readonly isNewCodeSent$: Observable<boolean> = this.globalStore.select(selectIsNewCodeSent);
  readonly noEmailFoundError$: Observable<{ noEmailFound: boolean }> = this.globalStore.select(selectNoEmailFoundError);
  readonly isIncorrectCodeError$: Observable<boolean> = this.globalStore.select(isIncorrectSignInCode);
  readonly lastSignInCode$: Observable<string> = this.globalStore.select(selectLastSignInCode);
  readonly signInCompanies$: Observable<HTTP_CP_ListSignInCompanies_Response_Company[]> = this.globalStore.select(selectSignInCompanies);
  readonly isLoadingSignInCompanies$: Observable<boolean> = this.globalStore.select(selectIsLoadingSignInCompanies);
  readonly isCheckingLoginEmail$: Observable<boolean> = this.globalStore.select(selectIsCheckingLoginEmail);

  navigateToCheckMail(): void {
    void this.router.navigate([paths.AUTH, paths.auth.LOGIN]);
  }

  resendVerificationCode(email: string): void {
    this.globalStore.dispatch(sendVerificationCode({ email, isResend: true }));
  }

  hideNewCodeSent(): void {
    this.globalStore.dispatch(hideNewCodeSent());
  }

  checkManualLoginCode(verificationCode: string): void {
    this.globalStore.dispatch(checkManualLoginCode({ verificationCode }));
  }

  clearAuthError(): void {
    this.globalStore.dispatch(authError({ errorCode: null }));
  }

  checkLoginMail(email: string): void {
    this.globalStore.dispatch(checkLoginMail({ email }));
  }

  loginWithToken(token: string, link: string): void {
    this.globalStore.dispatch(resetStore());
    this.globalStore.dispatch(loginWithToken({ token, redirectUrl: link }));
  }

  selectCompany(companyId: string): void {
    this.globalStore.dispatch(selectSignInCompany({ companyId }));
  }

  goToLoginPage(): void {
    this.authService.logout();
    void this.router.navigate(['/', paths.AUTH, paths.auth.LOGIN]);
  }
}
